import { PrimeReactProvider } from "primereact/api";
import { OrganizationChart } from 'primereact/organizationchart';
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { AuthContext } from '../context/AuthContext';
import { RateLimitContext } from '../context/RateLimitContext';
import api from '../lib/api';
import { cn } from '../lib/utils';
import { ScrollArea, ScrollBar } from './ui/scroll-area';
import { Skeleton } from './ui/skeleton';
import { toast } from './ui/use-toast';

const Tailwind = {
    organizationchart: {
        table: 'mx-auto my-0 border-spacing-0 border-separate',
        cell: 'text-center align-top py-0 px-3',
        node: {
            className: cn(
                'relative inline-block bg-white border border-gray-300 text-gray-600 p-5 hover:cursor-pointer hover:bg-gray-100',
                'dark:border-blue-900/40 dark:bg-gray-900 dark:text-white/80 hover:cursor-pointer' // Dark Mode
            )
        },
        linecell: 'text-center align-top py-0 px-3',
        linedown: {
            className: cn(
                'mx-auto my-0 w-px h-[20px] bg-gray-300',
                'dark:bg-blue-900/40' //Dark Mode
            )
        },
        lineleft: ({ context }) => ({
            className: cn(
                'text-center align-top py-0 px-3 rounded-none border-r border-gray-300',
                'dark:border-blue-900/40', //Dark Mode
                {
                    'border-t': context.lineTop
                }
            )
        }),
        lineright: ({ context }) => ({
            className: cn(
                'text-center align-top py-0 px-3 rounded-none',
                'dark:border-blue-900/40', //Dark Mode
                {
                    'border-t border-gray-300': context.lineTop
                }
            )
        }),
        nodecell: 'text-center align-top py-0 px-3',
        nodetoggler: {
            className: cn(
                'hidden',
                // 'absolute bottom-[-0.75rem] left-2/4 -ml-3 w-6 h-6 bg-inherit text-inherit rounded-full z-2 cursor-pointer no-underline select-none',
                // 'focus:outline-none focus:outline-offset-0 focus:shadow-[0_0_0_0.2rem_rgba(191,219,254,1)] dark:focus:shadow-[0_0_0_0.2rem_rgba(147,197,253,0.5)]' // Focus styles
            )
        },
        // nodetogglericon: 'relative inline-block w-4 h-4'
        nodeTogglerIcon: 'hidden'
    }
}

function transformFamilyTree(data, name) {
    // Get the first entry from familyTree
    const family = data.familyTree[0];

    // Create the final transformed structure
    return [
        {
            "label": `Kanin: ${name}`,
            "_bunny_id": family.bunny_id, // Add _bunny_id property
            "className": 'bg-red text-red-500',
            "expanded": true,
            "children": [
                {
                    "label": `Far: ${family.father_name || 'Okänd'}`,
                    "className": `${family.father_id ? '' : '!bg-white !hover:cursor-default'}`,
                    "_bunny_id": family.father_id, // Include the same _bunny_id at each level if required
                    "expanded": true,
                    "children": [
                        {
                            "label": `Farfar: ${family.father_father_name || 'Okänd'}`,
                            "className": `${family.father_father_id ? '' : '!bg-white !hover:cursor-default'}`,
                            "_bunny_id": family.father_father_id, // Include _bunny_id in grandchildren
                            "expanded": true,
                            "children": []
                        },
                        {
                            "label": `Farmor: ${family.father_mother_name || 'Okänd'}`,
                            "_bunny_id": family.father_mother_id, // Include _bunny_id in grandchildren
                            "expanded": true,
                            "children": []
                        }
                    ]
                },
                {
                    "label": `Mor: ${family.mother_name || 'Okänd'}`,
                    "_bunny_id": family.mother_id, // Add _bunny_id property for mother
                    "expanded": true,
                    "children": [
                        {
                            "label": `Morfar: ${family.mother_father_name || 'Okänd'}`,
                            "_bunny_id": family.mother_father_id, // Include _bunny_id in grandchildren
                            "expanded": true,
                            "children": []
                        },
                        {
                            "label": `Mormor: ${family.mother_mother_name || 'Okänd'}`,
                            "_bunny_id": family.mother_mother_id, // Include _bunny_id in grandchildren
                            "expanded": true,
                            "children": []
                        }
                    ]
                }
            ]
        }
    ];
}

const FamilyTree = ({ bunny }) => {
    const navigate = useNavigate();

    const [isLoading, setIsLoading] = useState(true);
    const [fetchedFamilyData, setFetchedFamilyData] = useState(null);
    const { auth } = useContext(AuthContext);
    const [error, setError] = useState();
    const [transformedData, setTransformedData] = useState();

    const { latestFamilyTreeFetch, setLatestFamilyTreeFetch } = useContext(RateLimitContext);

    const fetchFamilyInformation = async () => {
        // Fetch family data
        api.get(`https://skhrf-api.cmdigital.se/v1/bunnies/family-tree?bunnyId=${bunny.id}`)
            .then(response => {

                setFetchedFamilyData(response.data);
                setTransformedData(transformFamilyTree(response.data, bunny?.full_name));
                setIsLoading(false);
            })
            .catch(error => {
                console.error('Error fetching family data:', error);
                setError(error);
                setIsLoading(false);
            });
    }

    useEffect(() => {
        fetchFamilyInformation();
    }, [bunny]);

    const requestNewFamilyTree = (e) => {

        e.preventDefault();

        api.post(`https://skhrf-api.cmdigital.se/v1/bunnies/regenerate-family-tree`, { bunnyId: bunny.id })
            .then(response => {

                toast({
                    title: 'Släktträd begärt.',
                    description: response.data.success,
                })
            })
            .catch(error => {
                console.error('Error requesting new family tree:', error);
                setError(error);
                toast({
                    variant: 'destructive',
                    title: 'Något gick fel.',
                    description: error?.response?.data?.error,
                });
            });
    }

    function navigateToElderly(node) {

        // Check if the clicked bynny is known or not
        if (node.node._bunny_id == null) {
            toast({
                variant: 'destructive',
                title: 'Något gick fel.',
                description: 'Du kan inte gå till en okänd kanins profil.',
            });
            return;
        }

        // Check if the current time is less than the latest fetch time plus 3 seconds
        if (latestFamilyTreeFetch && Date.now() < latestFamilyTreeFetch + 3000) {
            toast({
                variant: 'destructive',
                title: 'Förfrågan blockerad.',
                description: 'Du kan endast göra en förfrågan var 3:e sekund.',
            });
            return;
        }

        // Update the latest fetch timestamp
        setLatestFamilyTreeFetch(Date.now());

        // Navigate to the selected bunny's profile
        navigate('/bunny/' + node.node._bunny_id);
    }

    return (
        isLoading ? (
            <Skeleton className='h-48 w-full' />
        ) : (
            fetchedFamilyData ? (
                <div className='rounded-md bg-gray-100 p-3'>
                    <PrimeReactProvider value={{ unstyled: true, pt: Tailwind }}>
                        <div>
                            <ScrollArea type='always'>
                                <OrganizationChart
                                    value={transformedData}
                                    style={{
                                        padding: 40,
                                        border: '1px solid rgb(226, 232, 240)',
                                        borderRadius: 6,
                                        backgroundColor: 'white',
                                    }}
                                    selectionMode='single'
                                    onNodeSelect={(node) => navigateToElderly(node)}
                                />
                                <ScrollBar orientation="horizontal" />
                            </ScrollArea>
                        </div>
                    </PrimeReactProvider>

                    {(auth.user?.id === bunny.owner || auth.user?.superadmin || auth.user?.bunnyadmin) && (
                        <p className='text-muted-foreground py-2'>Släktträd är statiska och måste genereras på nytt om en släkting till exempel byter namn. Du kan <span className='text-blue-600 hover:underline hover:cursor-pointer' onClick={requestNewFamilyTree}>klicka här</span> för att begära en ny generering av släktträdet för kaninen. Genereringen kan ta upp till 24 timmar.</p>
                    )}
                </div>
            ) : (
                <div className='rounded-md bg-gray-100 p-3'>Kom tillbaka om en stund. Det kan ta upp till 24 timmar att generera ett släktträd för en nyskapad kanin.</div>
            )
        )

    );
};
export default FamilyTree;